/**
 * Extension auth mode.
 *
 * Activated when the app is loaded with `?extension=true` (the extension modal
 * appends this to the iframe URL). In this mode the app does NOT use cookies;
 * instead it sends the JWT as an `Authorization: Bearer` header, and the token is
 * owned by the extension (stored in chrome.storage, fetched/persisted via the
 * message protocol in ./extensionBridge).
 */

import { getStoredToken, storeToken, clearStoredToken } from './extensionBridge'

// Detect ?extension=true once at module load (page load). Extension mode is
// determined purely by the current URL: no ?extension=true means normal mode.
const extensionMode = detectExtensionMode()

function detectExtensionMode() {
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get('extension') === 'true'
  } catch {
    return false
  }
}

// In-memory token cache so request headers can be built synchronously.
let cachedToken = null
// Dedupes concurrent loadToken() calls: a burst of parallel requests on page
// load shares a single STLMC_GET_TOKEN round-trip instead of firing one each.
let inflight = null

export function isExtensionMode() {
  return extensionMode
}

/** Returns the last cached token (sync). May be stale; prefer loadToken(). */
export function getToken() {
  return cachedToken
}

/** Fetch the token from the extension over the message protocol and cache it. */
export async function loadToken() {
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const res = await getStoredToken()
      cachedToken = res?.token ?? null
    } catch {
      cachedToken = null
    }
    return cachedToken
  })()
  try {
    return await inflight
  } finally {
    inflight = null
  }
}

/** Cache a token and persist it in the extension (after login). */
export async function setToken(token) {
  cachedToken = token
  await storeToken(token)
}

/** Clear the cached token and remove it from the extension (on logout). */
export async function clearToken() {
  cachedToken = null
  await clearStoredToken()
}
